import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-xl text-foreground-900 mb-2">分类未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="pt-20 pb-14 md:pb-20">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-800 font-medium">{category.name}</span>
            </nav>

            {/* Category Header */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-500">{category.count} 篇文章</p>
                </div>
              </div>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
                {category.description}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Article List */}
            <div className="space-y-4">
              {categoryArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
                </div>
              ) : (
                categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="group flex flex-col sm:flex-row gap-4 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {article.tags.map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                        <span className="text-xs text-foreground-500">{article.readTime}</span>
                      </div>
                      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-foreground-500">
                        <span>{article.author}</span>
                        <span>·</span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}